// Global social-stream state + transport, shared across the whole app via
// useState so it persists across route changes (the overlay lives in the layout,
// outside <NuxtPage>, so its connection isn't torn down on navigation).
//
// Live updates use Server-Sent Events with an automatic polling fallback. The
// SSE lifecycle is logged so failures are easy to diagnose. Message bodies are
// rendered as TEXT by the UI (never v-html); combined with server-side
// sanitising that keeps the feature XSS-safe.
export interface ChatMsg { id: number; kind: string; name: string; body: string; created_at: string | null }
export interface ChatFeed { enabled: boolean; can_post: boolean; latest_id: number; messages: ChatMsg[] }

export function useChat() {
  const api = useApi()
  const config = useRuntimeConfig()

  const open = useState<boolean>('chat-open', () => false)
  const messages = useState<ChatMsg[]>('chat-messages', () => [])
  const enabled = useState<boolean>('chat-enabled', () => true)
  const lastId = useState<number>('chat-last-id', () => 0)
  const transport = useState<'idle' | 'connecting' | 'live' | 'polling' | 'off'>('chat-transport', () => 'idle')
  const started = useState<boolean>('chat-started', () => false)

  // non-serialisable handles kept module-local (not in useState)
  function pushMessages(items: ChatMsg[]) {
    if (!items?.length) return
    const seen = new Set(messages.value.map(m => m.id))
    let changed = false
    for (const m of items) {
      if (!seen.has(m.id)) {
        messages.value.push(m)
        lastId.value = Math.max(lastId.value, m.id)
        changed = true
      }
    }
    if (messages.value.length > 400) messages.value = messages.value.slice(-400)
    return changed
  }

  function removeMessage(id: number) {
    messages.value = messages.value.filter(m => m.id !== id)
  }

  async function loadInitial() {
    try {
      const f = await api.get<ChatFeed>('/chat/messages')
      enabled.value = f.enabled
      messages.value = f.messages
      lastId.value = f.latest_id || (f.messages.at(-1)?.id ?? 0)
    } catch (e) {
      console.error('[chat] initial load failed', e)
    }
  }

  function startPolling(reason: string) {
    if (_pollTimer) return
    transport.value = 'polling'
    console.warn(`[chat] polling fallback (${reason})`)
    const tick = async () => {
      try {
        const f = await api.get<ChatFeed>(`/chat/messages?after=${lastId.value}`)
        enabled.value = f.enabled
        pushMessages(f.messages)
      } catch (e) { console.error('[chat] poll failed', e) }
    }
    _pollTimer = window.setInterval(tick, 4000)
    tick()
  }

  function stopPolling() {
    if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null }
  }

  function startSse() {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      startPolling('no EventSource'); return
    }
    const base = (config.public.apiBase as string).replace(/\/+$/, '')
    const url = `${base}/chat/stream?after=${lastId.value}`
    try {
      console.info('[chat] opening SSE', url)
      transport.value = 'connecting'
      _es = new EventSource(url, { withCredentials: true })
      _es.addEventListener('message', (ev: MessageEvent) => {
        try { pushMessages([JSON.parse(ev.data)]) } catch (e) { console.error('[chat] bad frame', e) }
      })
      _es.addEventListener('ready', () => { transport.value = 'live'; console.info('[chat] SSE live') })
      _es.onerror = (e) => {
        console.error('[chat] SSE error', e)
        if (transport.value !== 'live') { _es?.close(); _es = null; startPolling('SSE error before ready') }
      }
    } catch (e) {
      console.error('[chat] SSE construct failed', e)
      startPolling('SSE threw')
    }
  }

  // Idempotent: start the transport once for the app lifetime.
  async function ensureStarted() {
    if (started.value) return
    started.value = true
    await loadInitial()
    if (enabled.value) startSse()
    else transport.value = 'off'
  }

  function teardown() {
    _es?.close(); _es = null
    stopPolling()
    started.value = false
    transport.value = 'idle'
  }

  async function send(body: string): Promise<string | null> {
    const text = (body || '').trim()
    if (!text) return null
    try {
      const m = await api.post<ChatMsg>('/chat/messages', { body: text })
      pushMessages([m])
      return null
    } catch (e: any) {
      console.error('[chat] post failed', e)
      return e?.data?.detail || 'could not post your message'
    }
  }

  async function adminDelete(id: number): Promise<boolean> {
    try {
      await api.del(`/admin/chat/messages/${id}`)
      removeMessage(id)
      return true
    } catch (e) { console.error('[chat] delete failed', e); return false }
  }

  async function adminUserInfo(userId: number): Promise<any | null> {
    try { return await api.get(`/admin/chat/users/${userId}`) }
    catch (e) { console.error('[chat] user info failed', e); return null }
  }

  async function adminPurgeUser(userId: number): Promise<number> {
    try {
      const r = await api.del<{ deleted: number }>(`/admin/chat/users/${userId}/messages`)
      messages.value = messages.value.filter(m => m.user_id !== userId)
      return r?.deleted ?? 0
    } catch (e) { console.error('[chat] purge failed', e); return 0 }
  }

  async function adminMute(userId: number, minutes: number | null, reason = ''): Promise<boolean> {
    try { await api.post(`/admin/chat/users/${userId}/mute`, { minutes, reason }); return true }
    catch (e) { console.error('[chat] mute failed', e); return false }
  }

  async function adminUnmute(userId: number): Promise<boolean> {
    try { await api.del(`/admin/chat/users/${userId}/mute`); return true }
    catch (e) { console.error('[chat] unmute failed', e); return false }
  }

  async function adminCensor(word: string): Promise<boolean> {
    const w = (word || '').trim().toLowerCase()
    if (!w) return false
    try {
      await api.post('/admin/censor-words', { word: w })
      return true
    } catch (e) { console.error('[chat] censor add failed', e); return false }
  }

  return {
    open, messages, enabled, lastId, transport,
    ensureStarted, teardown, send, adminDelete, adminCensor, removeMessage,
    adminUserInfo, adminPurgeUser, adminMute, adminUnmute,
  }
}

// module-scoped transport handles (shared by all useChat() callers in the client)
let _es: EventSource | null = null
let _pollTimer: number | null = null
